// ============================================================
// feature-access-cache.ts
// ============================================================
// Persistence + invalidation rules behind useFeatureAccess.
//
// WHY THIS EXISTS: has_feature_access was the single busiest query in the
// product — 5,438 calls in one hour, ~10% of ALL database traffic, against a
// 60-connection instance that was sitting at 59 used. Two compounding causes:
//
//   1. NO IN-FLIGHT DEDUP. The old cache was a bare Map written only AFTER the
//      RPC resolved, so every component that mounted in the same tick missed it
//      and fired its own call. `useFeatureAccess('tasks')` is used in four
//      places = four identical concurrent round-trips, every mount.
//   2. MEMORY-ONLY. The Map died with the tab, so every app launch, reload and
//      OTA bundle swap re-paid the whole fan-out from cold. A push to 48 staff
//      meant 48 clients each doing that at once.
//
// The dedup lives in the hook (it owns the promises); this module owns the
// part worth testing on its own: the localStorage tier, per-feature freshness,
// and the invalidation matching. The value map lives under its own prefix,
// deliberately different from the PIN cache (use-feature-access-for-pin.ts),
// because that one stores plan-mapped keys and this one stores raw gate names.
//
// FRESHNESS IS PER FEATURE, NOT PER COMPANY. The blob's expiry is only how
// long we are willing to paint a saved answer. It used to be treated as "this
// whole map was just confirmed", and writeFeatureCache refreshes that expiry
// on every write, so confirming `tasks` kept a stale `employee_files` looking
// fresh for another six hours. A reload then painted the old answer and never
// asked again. confirmedAt is the clock that actually matters: it moves only
// for the feature that was just confirmed. A missing clock (a cache written
// before this existed) is stale, so the first reload after the change
// re-checks instead of trusting the shared expiry.

/** How long a saved answer may still be painted. Not a freshness window. */
export const FEATURE_CACHE_TTL_MS = 6 * 60 * 60 * 1000;

/**
 * While a tab stays open, a confirmed answer is re-checked once it is this
 * old. Three minutes is the "sitting on the page" bound: an override flipped
 * in the database shows up without a reload and without a sign-out.
 */
export const FEATURE_STALE_MS = 3 * 60 * 1000;

/**
 * A reload or a return to the tab re-checks once an answer is this old.
 * Short enough that coming back feels immediate. Long enough that alt-tabbing
 * and React's double mount do not each fire a query.
 */
export const FEATURE_FOCUS_STALE_MS = 60 * 1000;

const CACHE_PREFIX = 'feature_access_auth_v1_';
const EXPIRY_PREFIX = 'feature_access_auth_exp_v1_';
const CHECKED_PREFIX = 'feature_access_auth_checked_v1_';

/** The slice of the Storage API used here — lets tests pass a plain fake. */
export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  readonly length: number;
  key(index: number): string | null;
}

export type FeatureMap = Record<string, boolean>;

export interface CachedFeature {
  value: boolean;
  /**
   * When this feature's answer was last confirmed, epoch ms.
   * Null means the value is saved but no confirmation clock was recorded
   * (a cache written before per-feature freshness). Callers treat that as
   * stale and re-check, while still painting `value`.
   */
  confirmedAt: number | null;
}

export function cacheKeyFor(companyId: string): string {
  return `${CACHE_PREFIX}${companyId}`;
}
export function expiryKeyFor(companyId: string): string {
  return `${EXPIRY_PREFIX}${companyId}`;
}
export function checkedKeyFor(companyId: string): string {
  return `${CHECKED_PREFIX}${companyId}`;
}

function parseFeatureMap(raw: string | null): FeatureMap | null {
  if (!raw) return null;
  const parsed = JSON.parse(raw);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
  // Drop any non-boolean value rather than handing a truthy string to a gate.
  const clean: FeatureMap = {};
  for (const [k, v] of Object.entries(parsed)) {
    if (typeof v === 'boolean') clean[k] = v;
  }
  return clean;
}

function readCheckedMap(store: KeyValueStore, companyId: string): Record<string, number> {
  try {
    const raw = store.getItem(checkedKeyFor(companyId));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out: Record<string, number> = {};
    for (const [k, v] of Object.entries(parsed)) {
      const n = typeof v === 'number' ? v : typeof v === 'string' ? Number.parseInt(v, 10) : NaN;
      if (Number.isFinite(n)) out[k] = n;
    }
    return out;
  } catch {
    return {};
  }
}

/**
 * Every feature result known for `companyId`, or null when nothing usable is
 * cached or the blob's expiry has passed. An expired entry is evicted on read
 * so a client that never comes back for that company doesn't leave bytes behind.
 *
 * The hook does NOT use this for rendering. A past-expiry answer must still
 * paint while a re-check is in flight (see peekFeatureCache). This function
 * stays for callers that want the old hard miss, and for tests of that contract.
 *
 * Tolerant by design: a malformed or non-object payload reads as a miss rather
 * than throwing, because a corrupt cache must never be able to block a gate
 * check — the caller just pays one RPC and rewrites it.
 */
export function readFeatureCache(
  store: KeyValueStore | null | undefined,
  companyId: string | null | undefined,
  now: number,
): FeatureMap | null {
  if (!store || !companyId) return null;
  try {
    const expiry = store.getItem(expiryKeyFor(companyId));
    const expiresAt = expiry ? Number.parseInt(expiry, 10) : NaN;
    if (!Number.isFinite(expiresAt) || now > expiresAt) {
      store.removeItem(cacheKeyFor(companyId));
      store.removeItem(expiryKeyFor(companyId));
      store.removeItem(checkedKeyFor(companyId));
      return null;
    }
    return parseFeatureMap(store.getItem(cacheKeyFor(companyId)));
  } catch {
    return null;
  }
}

/**
 * The saved map even when the blob expiry has passed. Does not delete.
 * Rendering uses this so a re-check that fails can keep the last answer
 * instead of forgetting it and then failing closed.
 */
export function peekFeatureCache(
  store: KeyValueStore | null | undefined,
  companyId: string | null | undefined,
): FeatureMap | null {
  if (!store || !companyId) return null;
  try {
    return parseFeatureMap(store.getItem(cacheKeyFor(companyId)));
  } catch {
    return null;
  }
}

/** When `featureName` was last confirmed, or null if that clock was never written. */
export function featureConfirmedAt(
  store: KeyValueStore | null | undefined,
  companyId: string | null | undefined,
  featureName: string,
): number | null {
  if (!store || !companyId || !featureName) return null;
  const checked = readCheckedMap(store, companyId)[featureName];
  return checked === undefined ? null : checked;
}

/**
 * One saved gate, ignoring blob expiry. Null when this feature was never saved.
 */
export function readCachedFeature(
  store: KeyValueStore | null | undefined,
  companyId: string | null | undefined,
  featureName: string,
): CachedFeature | null {
  if (!featureName) return null;
  const map = peekFeatureCache(store, companyId);
  if (!map || !(featureName in map)) return null;
  return {
    value: map[featureName],
    confirmedAt: featureConfirmedAt(store, companyId, featureName),
  };
}

/**
 * True when a saved answer should be re-checked. A missing confirmation clock
 * is stale on purpose: the pre-change cache has only a company-wide expiry,
 * and that expiry moves whenever ANY feature is saved.
 */
export function isConfirmationStale(
  confirmedAt: number | null | undefined,
  now: number,
  staleAfterMs: number,
): boolean {
  if (confirmedAt === null || confirmedAt === undefined || !Number.isFinite(confirmedAt)) return true;
  return now - confirmedAt >= staleAfterMs;
}

/**
 * Merge `features` into the stored map and refresh the blob TTL. Merging
 * (rather than replacing) is what lets each gate write its own answer as it
 * resolves while still building up to one entry covering every feature the
 * client asked about.
 *
 * The blob TTL moving forward does NOT mark the other features fresh. Only
 * the keys in this write get a new confirmedAt. The next read of an untouched
 * key still paints the saved boolean and re-checks it in the background.
 */
export function writeFeatureCache(
  store: KeyValueStore | null | undefined,
  companyId: string | null | undefined,
  features: FeatureMap,
  now: number,
  ttlMs: number = FEATURE_CACHE_TTL_MS,
): void {
  if (!store || !companyId) return;
  try {
    const existing = peekFeatureCache(store, companyId) ?? {};
    const merged = { ...existing, ...features };
    store.setItem(cacheKeyFor(companyId), JSON.stringify(merged));
    store.setItem(expiryKeyFor(companyId), String(now + ttlMs));
    const checked = readCheckedMap(store, companyId);
    for (const [name, value] of Object.entries(features)) {
      if (typeof value === 'boolean') checked[name] = now;
    }
    store.setItem(checkedKeyFor(companyId), JSON.stringify(checked));
  } catch {
    // Quota exceeded / private mode / SSR — the in-memory tier still works.
  }
}

/**
 * Drop persisted results. No company = every company (the super-admin
 * "features changed" broadcast); a company = just that one.
 *
 * Note this clears the WHOLE company entry even when the caller named a single
 * feature. Feature overrides are set from one screen and are rare, so paying a
 * few RPCs to re-warm is the cheap side of the trade against leaving a stale
 * `true` on a gate someone just turned off.
 */
export function clearFeatureCache(
  store: KeyValueStore | null | undefined,
  companyId?: string | null,
): void {
  if (!store) return;
  try {
    if (companyId) {
      store.removeItem(cacheKeyFor(companyId));
      store.removeItem(expiryKeyFor(companyId));
      store.removeItem(checkedKeyFor(companyId));
      return;
    }
    const doomed: string[] = [];
    for (let i = 0; i < store.length; i++) {
      const key = store.key(i);
      if (
        key &&
        (key.startsWith(CACHE_PREFIX) || key.startsWith(EXPIRY_PREFIX) || key.startsWith(CHECKED_PREFIX))
      ) {
        doomed.push(key);
      }
    }
    for (const key of doomed) store.removeItem(key);
  } catch {
    // Ignore — see writeFeatureCache.
  }
}

/** Payload carried by the feature_access_invalidated event. */
export interface InvalidationDetail {
  companyId?: string;
  featureName?: string;
}

/**
 * Whether a mounted hook watching (companyId, featureName) should re-check in
 * response to an invalidation. An absent field in the event means "any", so a
 * bare invalidateFeatureAccess() re-checks everything. Extracted from the
 * hook's inline listener so the match rules are testable without a DOM.
 */
export function matchesInvalidation(
  detail: InvalidationDetail | null | undefined,
  companyId: string | null | undefined,
  featureName: string,
): boolean {
  if (!detail) return true;
  const companyMatches = !detail.companyId || detail.companyId === companyId;
  const featureMatches = !detail.featureName || detail.featureName === featureName;
  return companyMatches && featureMatches;
}
